import type { ActivityDefinition, Extension, ObservationDefinition } from "@medplum/fhirtypes";
import { resolveParameters, STANDARD_OBSERVATIONS, type TreatmentObservationDeclaration } from "@repo/domain";
import { CATALOG_VALUE_URL, decodeCatalogValue } from "../fhir/catalog-value";
import {
  diagnosticDefinitionAvailability,
  diagnosticDefinitionDescription,
  diagnosticDefinitionMintedAt,
  diagnosticDefinitionName,
  diagnosticDefinitionUrl,
  diagnosticDefinitionVersion,
  diagnosticDeviceRequirements,
  diagnosticLocationRequirement,
  diagnosticObservationDefinitions,
  diagnosticParameters,
  diagnosticProcedureNoteLabel,
  diagnosticStandardObservations,
  memberField,
  observationComponents,
  observationLabel,
  observationNoteLabel,
  observationRepeatable,
} from "../fhir/diagnostic-definition-storage";
import { isStandardParameterExtension, readStandardParameterReference } from "../fhir/standard-parameter-reference";
import type {
  CatalogDiagnosticObservationDto,
  DiagnosticComponentDto,
  DiagnosticDefinitionDto,
  DiagnosticDeviceRequirementDto,
} from "./diagnostic-definition";

/**
 * Row → DTO for the diagnostic catalog (ADR-0021).
 *
 * The row facts throw through the storage readers; a single MEMBER the stored
 * row cannot express is dropped instead. This runs inside a live query, where
 * a throw blanks the whole page — and a member written without its identity or
 * with a value at a schema version this reader does not know is a server
 * defect, which the server is where it fails loudly.
 */

function projectComponent(member: Extension): DiagnosticComponentDto | undefined {
  const id = memberField(member, "id")?.valueString;
  const label = memberField(member, "label")?.valueString;
  const value = decodeCatalogValue(memberField(member, CATALOG_VALUE_URL));
  if (!id || label === undefined || !value) return undefined;
  return { id, label, value };
}

function projectObservation(definition: ObservationDefinition): CatalogDiagnosticObservationDto | undefined {
  const id = definition.id;
  const label = observationLabel(definition);
  const repeatable = observationRepeatable(definition);
  if (!id || label === undefined || repeatable === undefined) return undefined;
  const noteLabel = observationNoteLabel(definition);
  return {
    id,
    label,
    repeatable,
    ...(noteLabel === undefined ? {} : { noteLabel }),
    components: observationComponents(definition).flatMap((member) => {
      const component = projectComponent(member);
      return component ? [component] : [];
    }),
  };
}

function projectParameter(member: Extension): DiagnosticDefinitionDto["parameters"][number] | undefined {
  if (isStandardParameterExtension(member)) return readStandardParameterReference(member);
  const id = memberField(member, "id")?.valueString;
  const label = memberField(member, "label")?.valueString;
  const value = decodeCatalogValue(memberField(member, CATALOG_VALUE_URL));
  if (!id || label === undefined || !value) return undefined;
  return { id, label, value };
}

function projectDeviceRequirement(member: Extension): DiagnosticDeviceRequirementDto | undefined {
  const id = memberField(member, "id")?.valueString;
  const definitionUrl = memberField(member, "definition")?.valueCanonical;
  if (!id || !definitionUrl) return undefined;
  return { id, definitionUrl };
}

export function projectDiagnosticDefinitionRow(resource: ActivityDefinition): DiagnosticDefinitionDto {
  const description = diagnosticDefinitionDescription(resource);
  const procedureNoteLabel = diagnosticProcedureNoteLabel(resource);
  const locationType = diagnosticLocationRequirement(resource);
  const standardObservations = diagnosticStandardObservations(resource);
  const observations = diagnosticObservationDefinitions(resource).flatMap((definition) => {
    const observation = projectObservation(definition);
    return observation ? [observation] : [];
  });

  const parameters = diagnosticParameters(resource).flatMap((member) => {
    const parameter = projectParameter(member);
    return parameter ? [parameter] : [];
  });
  return {
    definitionUrl: diagnosticDefinitionUrl(resource),
    ...(resource.id ? { resourceId: resource.id } : {}),
    version: diagnosticDefinitionVersion(resource),
    mintedAt: diagnosticDefinitionMintedAt(resource),
    name: diagnosticDefinitionName(resource),
    ...(description === undefined ? {} : { description }),
    availability: diagnosticDefinitionAvailability(resource),
    standardObservations,
    ...(procedureNoteLabel === undefined ? {} : { procedureNoteLabel }),
    observations,
    // What a clinician charts: the enabled standards first, then the authored
    // ones, mirroring the domain's own `chartedObservations`.
    chartedObservations: [
      ...standardObservations.map((key) => projectStandardObservation(STANDARD_OBSERVATIONS[key])),
      ...observations,
    ],
    effectiveParameters: resolveParameters(parameters),
    parameters,
    requirements: {
      devices: diagnosticDeviceRequirements(resource).flatMap((member) => {
        const requirement = projectDeviceRequirement(member);
        return requirement ? [requirement] : [];
      }),
      ...(locationType === undefined ? {} : { locationType }),
    },
  };
}

/**
 * A standard observation is declared by the platform rather than authored, so
 * its ids are already permanent (`stdobs`/`stdcmp`/`stdopt`) and it never
 * reaches storage at all.
 */
export function projectStandardObservation(
  observation: TreatmentObservationDeclaration,
): CatalogDiagnosticObservationDto {
  return {
    id: String(observation.id),
    label: observation.label,
    repeatable: observation.repeatable,
    ...(observation.noteLabel === undefined ? {} : { noteLabel: observation.noteLabel }),
    components: observation.components.map((component) => ({
      id: String(component.id),
      label: component.label,
      value:
        component.value.kind === "Choice"
          ? {
              ...component.value,
              options: component.value.options.map((option) => ({ id: String(option.id), label: option.label })),
            }
          : { ...component.value },
    })),
  };
}
